import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useCallback } from 'react';

import { UploadedReceipt } from '../../../types';
import {
  addReceiptsToBulkOperation,
  removeReceiptFromBulkOperation,
} from '../apis/bulkOperationApi';
import { BulkOperation } from '../apis/types';

export const useReceiptCollection = (bulkOperationId: string) => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  const [removingReceiptIds, setRemovingReceiptIds] = useState<Set<string>>(
    new Set()
  );

  interface AddReceiptsVariables {
    receiptIds: string[];
    receiptObjects?: UploadedReceipt[];
  }

  const addReceiptsMutation = useMutation({
    mutationFn: async ({ receiptIds }: AddReceiptsVariables) => {
      const token = await getAccessTokenSilently();
      return addReceiptsToBulkOperation(
        bulkOperationId,
        { receiptIds },
        token
      );
    },
    onSuccess: (updatedBulkOperation, { receiptObjects }) => {
      // Pre-populate new receipts cache key with existing + new receipt data
      // so the UI doesn't flash empty while polling catches up.
      const oldReceiptIds = queryClient.getQueryData<BulkOperation>(
        ['bulk-operation', bulkOperationId]
      )?.receiptIds || [];
      const currentReceipts = queryClient.getQueryData<UploadedReceipt[]>(
        ['uploaded-receipts', oldReceiptIds]
      ) || [];

      const allReceipts = [...currentReceipts];
      if (receiptObjects) {
        const existingIds = new Set(currentReceipts.map(r => r.id));
        for (const r of receiptObjects) {
          if (!existingIds.has(r.id)) allReceipts.push(r);
        }
      }

      queryClient.setQueryData(
        ['uploaded-receipts', updatedBulkOperation.receiptIds],
        allReceipts
      );
      queryClient.setQueryData(
        ['bulk-operation', bulkOperationId],
        updatedBulkOperation
      );
      queryClient.invalidateQueries({ queryKey: ['bulk-operations'] });
    },
  });

  const removeReceiptMutation = useMutation({
    mutationFn: async (receiptId: string) => {
      const token = await getAccessTokenSilently();
      return removeReceiptFromBulkOperation(
        bulkOperationId,
        receiptId,
        token
      );
    },
    onMutate: async (receiptId) => {
      // Cancel outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: ['bulk-operation', bulkOperationId] });

      const previousBulkOp = queryClient.getQueryData<BulkOperation>(
        ['bulk-operation', bulkOperationId]
      );
      const oldReceiptIds = previousBulkOp?.receiptIds || [];
      const newReceiptIds = oldReceiptIds.filter(id => id !== receiptId);

      // Pre-populate the new uploaded-receipts cache key with filtered data
      const currentReceipts = queryClient.getQueryData<UploadedReceipt[]>(
        ['uploaded-receipts', oldReceiptIds]
      );
      if (currentReceipts) {
        queryClient.setQueryData(
          ['uploaded-receipts', newReceiptIds],
          currentReceipts.filter(r => r.id !== receiptId)
        );
      }

      // Optimistically update the bulk operation (triggers query key change)
      queryClient.setQueryData<BulkOperation>(
        ['bulk-operation', bulkOperationId],
        old => old ? { ...old, receiptIds: newReceiptIds } : old
      );

      return { previousBulkOp };
    },
    onError: (_, _receiptId, context) => {
      // Rollback to previous bulk operation state
      if (context?.previousBulkOp) {
        queryClient.setQueryData(
          ['bulk-operation', bulkOperationId],
          context.previousBulkOp
        );
      }
    },
    onSuccess: (updatedBulkOperation) => {
      queryClient.setQueryData(
        ['bulk-operation', bulkOperationId],
        updatedBulkOperation
      );
      queryClient.invalidateQueries({ queryKey: ['bulk-operations'] });
    },
    onSettled: (_, __, receiptId) => {
      setRemovingReceiptIds(prev => {
        const next = new Set(prev);
        next.delete(receiptId);
        return next;
      });
    },
  });

  const removeReceipt = useCallback(
    async (receiptId: string) => {
      setRemovingReceiptIds(prev => new Set(prev).add(receiptId));
      await removeReceiptMutation.mutateAsync(receiptId);
    },
    [removeReceiptMutation]
  );

  const addReceipts = useCallback(
    (receiptIds: string[], receiptObjects?: UploadedReceipt[]) =>
      addReceiptsMutation.mutateAsync({ receiptIds, receiptObjects }),
    [addReceiptsMutation]
  );

  return {
    addReceipts,
    isAddingReceipt: addReceiptsMutation.isPending,
    addReceiptError: addReceiptsMutation.error?.message || null,
    removeReceipt,
    removingReceiptIds,
    removeReceiptError: removeReceiptMutation.error?.message || null,
  };
};
